/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MarkdownService } from '../../services/markdown.service';
import { ArticleService } from '../../services/article.service';

interface ArticleInstruction {
  type: 'success' | 'info' | 'warning' | 'danger';
  message: string;
}

/**
 * Single Article component that displays a full article with all metadata,
 * social features, and comment section
 */
@Component({
  selector: 'ut-single-article',
  templateUrl: './single-article.component.html',
  styleUrls: ['./single-article.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SingleArticleComponent implements OnInit {
  /**
   * Article data from route resolve
   */
  @Input() article!: any;

  /**
   * Instructions to display based on article state
   */
  instructions: ArticleInstruction[] = [];

  /**
   * Processed and sanitized article content
   */
  processedContent: SafeHtml = '';

  /**
   * Article service reference for comment section
   */
  service: any;

  constructor(
    private markdownService: MarkdownService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private ArticleService: ArticleService,
    @Inject('MetaService') private MetaService: any,
  ) {}

  ngOnInit(): void {
    // Store ArticleService reference for comment section
    this.service = this.ArticleService;

    // Update meta service for SEO
    this.updateMetaService();

    // Process markdown content and sanitize HTML
    if (this.article?.content) {
      const processedMarkdown = this.markdownService.process(this.article.content);
      this.processedContent = this.sanitizer.bypassSecurityTrustHtml(processedMarkdown);
    }

    // Set instruction messages based on article state
    this.setState(this.article?.state);

    // Mark for check to update view
    this.cdr.markForCheck();
  }

  /**
   * Update meta service with canonical URL for SEO
   */
  private updateMetaService(): void {
    if (this.article?.canonical) {
      this.MetaService.set('canonical', this.article.canonical);
    }
  }

  onArticleStateChanged(): void {
    this.article = { ...this.article };
    this.setState(this.article?.state);
    this.cdr.markForCheck();
  }

  private setState(state: string): void {
    switch (state) {
      case 'draft':
        this.instructions = [
          {
            type: 'warning',
            message: 'This article is not published yet',
          },
        ];
        break;
      case 'hidden':
        this.instructions = [
          {
            type: 'danger',
            message: 'This article is hidden',
          },
        ];
        break;
      case 'archived':
        this.instructions = [
          {
            type: 'warning',
            message:
              'This article has been archived. Its contents may be outdated. Please check the articles page for up-to-date content.',
          },
        ];
        break;
      case 'rejected':
        this.instructions = [
          {
            type: 'warning',
            message: `This article has been rejected for the following reason: ${
              this.article?.reject_reason || 'Unknown'
            }. Please update the article and re-submit.`,
          },
        ];
        break;
      case 'review':
        this.instructions = [
          {
            type: 'warning',
            message: 'Article is currently pending review.',
          },
        ];
        break;
      default:
        this.instructions = [];
        break;
    }
  }
}
